import React from 'react';
import Icone from './Icone';

/**
 * Bloco que abre e fecha (at-69, frente D/D7).
 *
 * 🔴 **Sobre `<details>`/`<summary>` NATIVOS, não `useState`.** Os dois padrões
 * conviviam no app fazendo a mesma coisa: "Regras do sistema" (`RegrasDeContrato`)
 * tinha `useState(aberto)` + `aria-expanded` e `+`/`−` como **único** sinal de
 * estado, enquanto a ficha da motocicleta elétrica usava `<details>` cru. O nativo
 * dá de graça o que o outro teve de escrever: o `<summary>` é focável, Enter e
 * Espaço alternam, o leitor de tela anuncia "expandido/recolhido", e o conteúdo
 * fechado continua alcançável pelo Ctrl+F do navegador.
 *
 * ⚠️ **Nenhum JS**, então nada a sincronizar: quem precisa saber o estado deve
 * ouvir o evento `toggle` do próprio elemento. Este componente não guarda estado.
 *
 * 📌 **Não há seletor de elemento `details`/`summary` no CSS legado** (grep vazio
 * em 11/09/2026, incluindo `legado-compartilhado.css`), então não há cascata a
 * vencer — diferente do `input[type="text"]` que obrigou o marcador do `Input` e
 * do `input[type="checkbox"]` que obrigou o desenho do `Checkbox`. Se um dia
 * aparecer um, este componente precisa do mesmo tratamento.
 *
 * ⚠️ **O marcador é o chevron do `Icone`, girando por `group-open:`** — não o
 * `+`/`−` de antes, que era pictograma sem texto, e não o triângulo nativo do
 * navegador (removido com `marker:hidden` + `[&::-webkit-details-marker]`, porque
 * ele muda de forma entre navegadores e não acompanha a borda arredondada, pelo
 * mesmo motivo do `appearance: none` do `Select`).
 */

interface PropsRecolhivel {
  /** Vai no `<summary>` — é o alvo de clique e o que o leitor de tela anuncia. */
  titulo: string;
  /** Linha suave ao lado do título (ex.: "9 regras"). Opcional. */
  resumo?: React.ReactNode;
  /** Nasce aberto. Padrão `false`: o bloco existe para encurtar a rolagem. */
  abertoInicial?: boolean;
  className?: string;
  children: React.ReactNode;
}

const Recolhivel: React.FC<PropsRecolhivel> = ({
  titulo,
  resumo,
  abertoInicial = false,
  className = '',
  children,
}) => (
  <details
    open={abertoInicial}
    className={[
      'tw-group tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    <summary
      className={[
        'tw-flex tw-cursor-pointer tw-list-none tw-items-center tw-gap-2 tw-px-4 tw-py-3',
        'tw-text-sm tw-font-semibold tw-text-texto-titulo',
        'tw-rounded-xl group-open:tw-rounded-b-none hover:tw-bg-superficie',
        'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand/40',
        /* O triângulo nativo, nos dois motores. */
        'marker:tw-hidden [&::-webkit-details-marker]:tw-hidden',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className="tw-shrink-0 tw-text-texto-suave tw-transition-transform group-open:tw-rotate-180 motion-reduce:tw-transition-none"
      >
        <Icone nome="chevron-baixo" tamanho={16} />
      </span>

      <span className="tw-min-w-0 tw-flex-1">{titulo}</span>

      {resumo && (
        <span className="tw-shrink-0 tw-text-xs tw-font-normal tw-text-texto-suave">
          {resumo}
        </span>
      )}
    </summary>

    <div className="tw-border-t tw-border-superficie-borda tw-px-4 tw-py-3">{children}</div>
  </details>
);

export default Recolhivel;
